import { Component, EventEmitter, Input, Output, AfterViewInit, ViewChild, ElementRef, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  Chart as ChartJS,
  ChartConfiguration,
  ArcElement,
  DoughnutController,
  Tooltip,
  Legend
} from 'chart.js';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';

// Register Chart.js components for doughnut chart
ChartJS.register(
  ArcElement,
  DoughnutController,
  Tooltip,
  Legend
);

export interface SpendingCategory {
  label: string;
  color: string;
  percentage: number;
  amount: string;
  icon?: string;
}

@Component({
  selector: 'app-spending-snapshot-widget',
  standalone: true,
  imports: [CommonModule, WidgetEdit, OverlayModule],
  templateUrl: './spending-snapshot-widget.html',
  styleUrl: './spending-snapshot-widget.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpendingSnapshotWidgetComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() isEditMode: boolean = false;
  @Input() isAddMode: boolean = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';
  @Input() title: string = 'Spending Breakdown';
  @Input() index = 0;
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;

  private resizeObserver: ResizeObserver | null = null;
  private resizeDebounce: any = null;
  private rafId: number = 0;


  @Input() categories: SpendingCategory[] = [
    { label: 'Dining', color: '#EF4D6C', percentage: 32, amount: '₹60,640.35' },
    { label: 'Groceries', color: '#E7733A', percentage: 32, amount: '₹89,300' },
    { label: 'Transport', color: '#52A8FB', percentage: 32, amount: '₹6,640' },
    { label: 'Shopping', color: '#9875FF', percentage: 5, amount: '₹600' },
    { label: 'Entertainment', color: '#F9A000', percentage: 4, amount: '₹400' },
    { label: 'Others', color: 'rgba(99, 104, 111, 0.8)', percentage: 0, amount: '₹0' }
  ];
  @Output() editCall = new EventEmitter<any>();
  @Output() viewAllCall = new EventEmitter<void>();

  @ViewChild('donutCanvas') donutCanvas!: ElementRef<HTMLCanvasElement>;
  private chart: ChartJS | null = null;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit() {
    this.setupResizeObserver();
    // Use RAF to ensure chart initialization happens after initial layout
    this.rafId = requestAnimationFrame(() => {
      this.initializeChart();
      this.updateSmallCategoryGap();
    });
  }

  private setupResizeObserver() {
    // Find the parent article.widget element
    const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;

    if (!widgetElement) {
      if (!this.isAddMode) {
        console.warn('Could not find parent widget element');
      }
      return;
    }

    // Set initial height after layout is complete
    this.rafId = requestAnimationFrame(() => {
      this.updateChartContainerHeight();
      if (this.chart) this.chart.resize();
    });

    this.resizeObserver = new ResizeObserver(() => {
      // Debounce resize events to prevent blocking main thread during drag
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = setTimeout(() => {
        const isDragging = document.body.classList.contains('is-resizing');
        const resizingWidgetType = document.body.dataset['resizingWidget'];

        // Only update if not dragging or if THIS widget type is being resized
        if (!isDragging || resizingWidgetType === 'spending') {
          this.updateChartContainerHeight();
          if (this.chart) this.chart.resize();
          this.updateSmallCategoryGap();
          this.updateMediumCategoryGap();
          this.cdr.markForCheck();
        }
      }, 16); // ~1 frame debounce
    });

    this.resizeObserver.observe(widgetElement);
  }

  private updateSmallCategoryGap() {
    // Only apply to small size layout
    if (this.size !== 'small') {
      return;
    }

    // Get the widget container's actual height
    const containerElement = this.elementRef.nativeElement as HTMLElement;
    if (!containerElement) return;

    // Get height from parent article.widget element (which has inline style height)
    const widgetElement = containerElement.closest('article.widget') as HTMLElement;
    if (!widgetElement) {
      return;
    }

    // Get the list element to apply gap to
    const listElement = containerElement.querySelector('.small-categories-list') as HTMLElement;
    if (!listElement) {
      return;
    }

    const widgetHeight = widgetElement.offsetHeight;

    // Define min/max heights and corresponding gap values
    const minHeight = 226; // min-height 
    const maxHeight = 331; // max height from config
    const minGap = 6;      // minimum gap in px
    const maxGap = 16;     // maximum gap in px



    if (widgetHeight <= minHeight) {
      // At or below minimum height: remove inline style to use CSS default
      listElement.style.removeProperty('gap');

    } else {
      // Above minimum height: calculate and apply dynamic gap
      const heightRatio = Math.min((widgetHeight - minHeight) / (maxHeight - minHeight), 1);
      const dynamicGap = minGap + (maxGap - minGap) * heightRatio;
      listElement.style.gap = `${dynamicGap}px`;

    }
  }

  private updateMediumCategoryGap() {
    // Only apply to medium/half/full size layouts
    if (this.size === 'small') {
      return;
    }

    // Get the widget container's actual height
    const containerElement = this.elementRef.nativeElement as HTMLElement;
    if (!containerElement) return;

    // Get height from parent article.widget element (which has inline style height)
    const widgetElement = containerElement.closest('article.widget') as HTMLElement;
    if (!widgetElement) {
      return;
    }

    // Get the breakdown-list element to apply gap to
    const listElement = containerElement.querySelector('.breakdown-list') as HTMLElement;
    if (!listElement) {
      return;
    }

    const widgetHeight = widgetElement.offsetHeight;

    // Define min/max heights and corresponding gap values based on size
    const minHeight = 226; // min-height 
    const maxHeight = 400; // max height for medium/half/full
    let minGap: number;
    let maxGap: number;

    // Different base gaps for different sizes (from SCSS)
    if (this.size === 'medium') {
      minGap = 12;
      maxGap = 20;
    } else if (this.size === 'half') {
      minGap = 16;
      maxGap = 24;
    } else { // full
      minGap = 20;
      maxGap = 28;
    }



    if (widgetHeight <= minHeight) {
      // At or below minimum height: remove inline style to use CSS default
      listElement.style.removeProperty('gap');

    } else {
      // Above minimum height: calculate and apply dynamic gap
      const heightRatio = Math.min((widgetHeight - minHeight) / (maxHeight - minHeight), 1);
      const dynamicGap = minGap + (maxGap - minGap) * heightRatio;
      listElement.style.gap = `${dynamicGap}px`;
    }
  }


  private updateChartContainerHeight() {
    // Only for medium/half/full sizes (small doesn't need this)
    if (this.size === 'small') return;

    const containerElement = this.elementRef.nativeElement as HTMLElement;
    if (!containerElement) return;

    // Get height from parent article.widget element
    const widgetElement = containerElement.closest('article.widget') as HTMLElement;
    if (!widgetElement) return;

    // Find the chart-container element
    const chartContainer = containerElement.querySelector('.chart-container') as HTMLElement;
    if (!chartContainer) return;

    // Get the widget height and calculate proportional chart height
    const widgetHeight = widgetElement.offsetHeight;
    // Leave some space for header and padding (roughly 40px header + padding)
    const availableHeight = Math.max(140, widgetHeight - 60);

    // Set the chart container height
    chartContainer.style.height = `${availableHeight}px`;

  }


  ngOnChanges(changes: SimpleChanges) {
    // Re-initialize chart when widget size or categories change
    if ((changes['size'] || changes['categories']) && !changes['size']?.firstChange) {
      this.destroyChart();
      // RAF ensures re-init happens when browser is ready to paint
      this.rafId = requestAnimationFrame(() => {
        this.initializeChart();
      });
    }
  }

  private initializeChart() {
    // Ensure any existing chart is destroyed first
    this.destroyChart();

    if (!this.donutCanvas) return;

    const canvas = this.donutCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    if (ctx) {

      // Filter categories with percentage > 0
      const activeCategories = this.categories.filter(cat => cat.percentage > 0);

      const chartConfig: ChartConfiguration<'doughnut'> = {
        type: 'doughnut',
        data: {
          labels: activeCategories.map(cat => cat.label),
          datasets: [{
            data: activeCategories.map(cat => cat.percentage),
            backgroundColor: activeCategories.map(cat => cat.color),
            borderWidth: 0,
            borderRadius: 0,
            spacing: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => {
                  const category = activeCategories[context.dataIndex];
                  return `${category.amount} (${category.percentage}%)`;
                }
              }
            }
          },
          rotation: -90
        }
      };

      this.chart = new ChartJS(ctx, chartConfig);
    }
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  emitEditEvent(event: any): void {
    this.editCall.emit(event);
  }

  onViewAll(): void {
    if (this.isAddMode) return;
    this.viewAllCall.emit();
  }

  // Get visible categories (non-zero percentages for small layout)
  get visibleCategories(): SpendingCategory[] {
    const sortedCategories = [...this.categories].sort((a, b) => {
      // b.percentage - a.percentage sorts in descending order (highest first)
      return b.percentage - a.percentage;
    });

    // 2. Slice the sorted array to get the first 3 elements
    const topThreeCategories = sortedCategories.slice(0, 3);
    if (this.size === 'small') {
      return this.categories.filter(cat => cat.percentage > 0).slice(0, 3);
    }
    return topThreeCategories.filter(cat => cat.percentage > 0);
  }

  // Check if we need to show "Others" row
  get showOthersRow(): boolean {
    const visibleCount = this.categories.filter(cat => cat.percentage > 0).length;
    return this.size !== 'small' && visibleCount > 3;
  }

  ngOnDestroy() {
    clearTimeout(this.resizeDebounce);
    cancelAnimationFrame(this.rafId);
    this.destroyChart();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}
